import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import { toIsoOrUndefined, toLocalInputValue } from '../../lib/datetimeLocal';
import type { CampaignRequest, CampaignResponse } from '../../model/types';

export interface CampaignFormDialogProps {
  open: boolean;
  editing?: CampaignResponse;
  saving: boolean;
  onClose: () => void;
  onSubmit: (body: CampaignRequest) => void;
}

interface Draft {
  code: string;
  name: string;
  description: string;
  startsAt: string;
  endsAt: string;
}

const EMPTY: Draft = { code: '', name: '', description: '', startsAt: '', endsAt: '' };

/** "New campaign" / edit dialog (plan-011 §8.1). */
export function CampaignFormDialog({ open, editing, saving, onClose, onSubmit }: CampaignFormDialogProps) {
  const { t } = useTranslation('promotions');
  const [draft, setDraft] = useState<Draft>(EMPTY);

  useEffect(() => {
    if (open) {
      setDraft(
        editing
          ? {
              code: editing.code,
              name: editing.name,
              description: editing.description ?? '',
              startsAt: toLocalInputValue(editing.startsAt),
              endsAt: toLocalInputValue(editing.endsAt),
            }
          : EMPTY,
      );
    }
  }, [open, editing]);

  const endBeforeStart =
    draft.startsAt.trim() && draft.endsAt.trim() && new Date(draft.endsAt) < new Date(draft.startsAt);
  const isValid = draft.code.trim().length > 0 && draft.name.trim().length > 0 && !endBeforeStart;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{editing ? t('campaignDialog.editTitle') : t('campaignDialog.newTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label={t('campaignDialog.codeLabel')}
            required
            disabled={Boolean(editing)}
            value={draft.code}
            onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value.toUpperCase() }))}
            helperText={t('campaignDialog.codeHint')}
            fullWidth
          />
          <TextField
            label={t('campaignDialog.nameLabel')}
            required
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            fullWidth
          />
          <TextField
            label={t('campaignDialog.descriptionLabel')}
            multiline
            minRows={2}
            value={draft.description}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            fullWidth
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('campaignDialog.startsAtLabel')}
              type="datetime-local"
              value={draft.startsAt}
              onChange={(e) => setDraft((d) => ({ ...d, startsAt: e.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
            <TextField
              label={t('campaignDialog.endsAtLabel')}
              type="datetime-local"
              value={draft.endsAt}
              onChange={(e) => setDraft((d) => ({ ...d, endsAt: e.target.value }))}
              error={Boolean(endBeforeStart)}
              helperText={endBeforeStart ? t('campaignDialog.endBeforeStartError') : undefined}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!isValid || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
          onClick={() =>
            onSubmit({
              code: draft.code.trim(),
              name: draft.name.trim(),
              description: draft.description.trim() || undefined,
              startsAt: toIsoOrUndefined(draft.startsAt) ?? null,
              endsAt: toIsoOrUndefined(draft.endsAt) ?? null,
            })
          }
        >
          {editing ? t('common:action.save') : t('common:action.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
